import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";
import { devError } from "./log";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (e) {
      // 401/403 is expected when there's no valid session — silently clear.
      // Network errors etc. are logged so we don't lose signal entirely.
      if (e?.response?.status && ![401, 403].includes(e.response.status)) {
        devError("auth.checkAuth failed:", e);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // The /auth/google page itself will exchange the code and call setUser.
    if (window.location.pathname === "/auth/google" && window.location.search.includes("code=")) {
      setLoading(false);
      return;
    }
    // v2.97i — Always call /auth/me on mount. The earlier "skip if no cookie
    // + no localStorage token" gate was broken: the session cookie is
    // httpOnly (invisible to JS) so `hasCookie` was always false, and
    // Safari ITP can purge localStorage.fm_token after a cross-site OAuth
    // redirect, so `hasToken` could be false too — and the user got logged
    // out on every refresh because /auth/me was never invoked. Letting it
    // run lets the cookie (sent via `withCredentials: true`) OR the Bearer
    // header authenticate the session; a 401 silently drops to guest state.
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    // Best-effort server logout; always clear local state even if the request fails
    try { await api.post("/auth/logout"); } catch (_e) { /* noop — local cleanup below */ }
    localStorage.removeItem("fm_token");
    setUser(null);
  };

  // Memoize context value so consumers don't re-render on every parent render
  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout }),
    [user, loading, checkAuth]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
